class MegaMenu extends HTMLElement {
  constructor() {
    super()
    this.mainMenu = this.querySelector('#main-menu')
    this.leftMenu = this.querySelector('#left-menu')
    this.rightMenu = this.querySelector('#right-menu')
    this.search = this.querySelector('#search')
    this.cta = this.querySelector('#cta')
    this.dropdownMenu = this.querySelector('#dropdown-menu')
    this.dropdownItems = this.querySelectorAll('[data-has-dropdown="true"]')
    this.activeDropdown = null // Track the currently active dropdown
    this.isAnimating = false // Track animation state

    // Header variables
    this.header = document.querySelector('header')
    this.headerHeight = 0
    this.scrollThreshold = 100 // Pixels to scroll before sticky
    this.isSticky = false
    this.lastScrollTop = 0
    this.ticking = false

    // Check for reduced motion preference
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // Add click outside listener
    document.addEventListener('click', e => {
      // If there's an active dropdown and the click is outside the mega menu
      if (this.activeDropdown && !this.contains(e.target)) {
        this.toggleDropdown(this.activeDropdown)
      }
    })
  }

  connectedCallback() {
    this.setupDropdowns()
    this.setupStickyHeader()
    this.setupMobileMenu()
  }

  setupDropdowns() {
    this.dropdownItems.forEach(item => {
      const dropdownId = item.getAttribute('data-dropdown-id')
      const menuLink = item.querySelector('a')

      if (!dropdownId) return

      let hoverIntentTimeout = null
      const HOVER_DELAY_MS = 100 // Delay for hiding on mouseleave

      const openDropdown = () => {
        // If this dropdown is already active and its trigger is marked active, do nothing.
        if (
          this.activeDropdown === dropdownId &&
          item.getAttribute('data-dropdown-active') === 'true'
        ) {
          return
        }

        // If another dropdown is active, hide it and mark its trigger inactive.
        if (this.activeDropdown && this.activeDropdown !== dropdownId) {
          const oldActiveItem = this.querySelector(`[data-dropdown-id="${this.activeDropdown}"]`)
          if (oldActiveItem) {
            oldActiveItem.setAttribute('data-dropdown-active', 'false')
          }
          this.hideDropdown(this.activeDropdown)
        }

        this.showDropdown(dropdownId) // This sets this.activeDropdown = dropdownId internally.

        // Mark this item active and ensure all others are marked inactive for their triggers.
        this.dropdownItems.forEach(di => {
          di.setAttribute('data-dropdown-active', di === item ? 'true' : 'false')
        })
      }

      const closeDropdown = () => {
        if (this.activeDropdown === dropdownId) {
          this.hideDropdown(dropdownId)
        }
      }

      item.addEventListener('mouseenter', () => {
        clearTimeout(hoverIntentTimeout)
        openDropdown()
      })

      item.addEventListener('mouseleave', () => {
        clearTimeout(hoverIntentTimeout)
        hoverIntentTimeout = setTimeout(() => {
          const dropdownContentElement = document.querySelector(
            `[data-dropdown-content="${dropdownId}"]`
          )
          if (dropdownContentElement && dropdownContentElement.matches(':hover')) {
            // Mouse is over the content, don't close yet.
            return
          }
          closeDropdown()
        }, HOVER_DELAY_MS)
      })

      const dropdownContent = document.querySelector(`[data-dropdown-content="${dropdownId}"]`)

      if (dropdownContent) {
        dropdownContent.addEventListener('mouseenter', () => {
          clearTimeout(hoverIntentTimeout) // Mouse entered content, cancel hide.
        })

        dropdownContent.addEventListener('mouseleave', () => {
          clearTimeout(hoverIntentTimeout)
          hoverIntentTimeout = setTimeout(() => {
            if (item.matches(':hover')) {
              // Mouse moved back to the trigger item, don't close.
              return
            }
            closeDropdown()
          }, HOVER_DELAY_MS)
        })
      }

      // Prevent default click action on links that trigger dropdowns.
      if (menuLink) {
        menuLink.addEventListener('click', e => {
          if (item.getAttribute('data-has-dropdown') === 'true') {
            e.preventDefault()
          }
        })
      }
    })
  }

  showDropdown(id) {
    // Get the dropdown content element
    const dropdown = document.querySelector(`[data-dropdown-content="${id}"]`)
    if (!dropdown) {
      console.error(`Dropdown with id ${id} not found in the DOM`)
      return
    }

    // Remove inline display: none if present
    dropdown.style.removeProperty('display')

    // Remove close animation class if present
    dropdown.classList.remove('dropdown-close')

    // Add open animation class
    dropdown.classList.add('dropdown-open')

    // Get all dropdown items
    const dropdownItems = dropdown.querySelectorAll('.dropdown-item')

    // Reset any existing animations
    dropdownItems.forEach(item => {
      item.style.opacity = '0'
      item.classList.remove('animate')
    })

    // Calculate stagger delay based on number of items
    // Set a maximum total animation time (1000ms), and divide by item count
    // but never go below a minimum threshold
    const itemCount = dropdownItems.length
    const maxTotalTime = this.prefersReducedMotion ? 400 : 1000 // 1 second total or 400ms for reduced motion
    const minDelay = this.prefersReducedMotion ? 50 : 100 // Minimum delay per item

    // Calculate delay based on number of items, but never go below minimum
    const staggerDelay = Math.max(minDelay, maxTotalTime / (itemCount || 1))

    dropdownItems.forEach((item, index) => {
      setTimeout(() => {
        item.classList.add('animate')
      }, index * staggerDelay)
    })

    // Set active state
    this.dropdownMenu.setAttribute('data-dropdown-active', 'true')
    this.activeDropdown = id
    this.isAnimating = false
  }

  hideDropdown(id) {
    // Hide dropdown
    // Get the dropdown content element
    const dropdown = document.querySelector(`[data-dropdown-content="${id}"]`)
    if (!dropdown) return

    // Reset animation states for dropdown items
    const dropdownItems = dropdown.querySelectorAll('.dropdown-item')
    dropdownItems.forEach(item => {
      item.classList.remove('animate')
    })

    // Remove open animation class if present
    dropdown.classList.remove('dropdown-open')

    // Add close animation class
    dropdown.classList.add('dropdown-close')

    const animationDuration = this.prefersReducedMotion ? 100 : 300

    // Set a timeout to match animation duration before fully closing
    setTimeout(() => {
      if (this.activeDropdown === id) {
        this.dropdownMenu.setAttribute('data-dropdown-active', 'false')
        this.activeDropdown = null
        // Deactivate the trigger item
        const triggerItem = this.querySelector(`[data-dropdown-id="${id}"]`)
        if (triggerItem) {
          triggerItem.setAttribute('data-dropdown-active', 'false')
        }
      }
      this.isAnimating = false
    }, animationDuration)
  }

  toggleDropdown(id) {
    // Prevent multiple animations running at once
    if (this.isAnimating) return
    this.isAnimating = true

    // If this is the active dropdown, close it
    if (id === this.activeDropdown) {
      this.dropdownMenu.setAttribute('data-dropdown-active', 'false')
      this.activeDropdown = null
      this.hideDropdown(id)
    }
    // If another dropdown is active, close it and open this one
    else if (this.activeDropdown) {
      const currentId = this.activeDropdown

      // First close the current dropdown
      this.dropdownMenu.setAttribute('data-dropdown-active', 'false')
      this.hideDropdown(currentId)

      // Calculate appropriate delay based on motion preferences
      const sequenceDelay = this.prefersReducedMotion ? 50 : 200

      // Then open the new dropdown after a slight delay for sequential animation
      setTimeout(() => {
        this.showDropdown(id)
      }, sequenceDelay)
    }
    // No active dropdown, simply open this one
    else {
      this.showDropdown(id)
      this.isAnimating = false
    }
  }

  setupStickyHeader() {
    // Store initial header height
    this.headerHeight = this.header.offsetHeight

    // Set up scroll event listener
    window.addEventListener('scroll', () => {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll()
          this.ticking = false
        })
        this.ticking = true
      }
    })

    // Handle resize events to recalculate header height
    window.addEventListener('resize', () => {
      if (!this.isSticky) {
        this.headerHeight = this.header.offsetHeight
      }
    })
  }

  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    // Determine scroll direction
    this.lastScrollTop = scrollTop
    if (scrollTop > this.scrollThreshold) {
      if (!this.isSticky) {
        // Set a CSS variable for the header height before making it sticky
        document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`)
        document.body.classList.add('has-sticky-header')
        this.header.classList.add('sticky-header')
        this.mainMenu.classList.add('pb-20')
        this.isSticky = true
      }
    } else {
      if (this.isSticky) {
        this.header.classList.remove('sticky-header')
        this.mainMenu.classList.remove('pb-20')
        document.body.classList.remove('has-sticky-header')
        this.isSticky = false
      }
    }
  }

  setupMobileMenu() {
    const mobileMenuTrigger = this.querySelector('#mobile-menu-trigger')
    const mobileMenu = this.querySelector('#mobile-menu')
    const hamburgerIcon = mobileMenuTrigger.querySelector('svg')

    // Initialize the mobile menu state
    mobileMenu.style.maxHeight = '0px'
    let isMobileMenuOpen = false

    mobileMenuTrigger.addEventListener('click', () => {
      isMobileMenuOpen = !isMobileMenuOpen

      if (isMobileMenuOpen) {
        mobileMenu.style.maxHeight = '100vh' // Use viewport height for better control
        hamburgerIcon.classList.add('rotate-90') // Add rotation for X effect if desired
        document.body.classList.add('overflow-hidden') // Add class to body to prevent scrolling
        document.body.classList.add('fixed') // Add class to body to prevent scrolling
      } else {
        mobileMenu.style.maxHeight = '0px'
        hamburgerIcon.classList.remove('rotate-90')
        document.body.classList.remove('overflow-hidden') // Remove class from body to re-enable scrolling
        document.body.classList.remove('fixed') // Remove class from body to re-enable scrolling
      }
    })
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu)
}
